import { Link, useParams, Navigate } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { ArrowRight, ArrowLeft, Clock, Tag, ChevronRight } from 'lucide-react';
import { blogPosts, services } from '../mockData';

const SITE_URL = 'https://www.hexalogixautomation.com';

export const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.id !== post.id).slice(0, 3);
  const relatedServices = services.filter((service) => (post.relatedServices || []).includes(service.slug));
  const pageUrl = `${SITE_URL}/blog/${post.slug}`;
  const publishedDate = new Date(post.date).toISOString();
  const articleSchemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      articleSection: post.category,
      datePublished: publishedDate,
      dateModified: publishedDate,
      mainEntityOfPage: pageUrl,
      url: pageUrl,
      image: ['https://www.hexalogixautomation.com/hero-desktop.webp'],
      author: {
        '@type': 'Organization',
        name: 'Hexalogix Automation'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Hexalogix Automation',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.hexalogixautomation.com/Hexalogix.png'
        }
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: pageUrl }
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title={`${post.title} | Hexalogix Automation`}
        description={post.excerpt}
        keywords={`${post.category}, industrial automation UAE, hexalogix automation`}
        ogType="article"
        canonical={pageUrl}
        schemas={articleSchemas}
      />

      <section className={`relative bg-gradient-to-br ${post.color} pt-[106px]`}>
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#0057B8]" />
        <div
          className="absolute top-0 right-0 w-96 h-96 bg-[#0057B8]/10"
          style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-300 truncate max-w-[200px]">{post.title}</span>
          </nav>

          <div className="flex items-center gap-3 mb-5">
            <span className="bg-[#0057B8] text-white text-sm font-bold tracking-widest uppercase px-3 py-1 flex items-center gap-1">
              <Tag className="w-3 h-3" />{post.category}
            </span>
            <span className="flex items-center gap-1 text-gray-400 text-sm">
              <Clock className="w-3 h-3" />{post.readTime}
            </span>
            <span className="text-gray-500 text-sm">{post.date}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-6">
            {post.title}
          </h1>
          <p className="text-gray-300 text-lg leading-relaxed max-w-3xl">
            {post.excerpt}
          </p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose-article">
            {post.content.map((block, i) => {
              if (block.type === 'heading') {
                return (
                  <h2 key={i} className="text-2xl font-black text-[#0A0F1E] mt-10 mb-4 pb-2 border-b-2 border-[#0057B8]/20">
                    {block.text}
                  </h2>
                );
              }
              if (block.type === 'paragraph') {
                return (
                  <p key={i} className="text-gray-700 text-base leading-relaxed mb-5">
                    {block.text}
                  </p>
                );
              }
              if (block.type === 'list') {
                return (
                  <ul key={i} className="mb-5 space-y-2">
                    {block.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-gray-700 text-base">
                        <span className="w-1.5 h-1.5 bg-[#0057B8] rounded-full mt-2.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              }
              return null;
            })}
          </article>

          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <Link to="/blog" className="flex items-center gap-2 text-[#0057B8] hover:text-[#003D82] font-semibold text-base transition-colors group">
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Back to Blog
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <button className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[#0057B8] hover:bg-[#003D82] text-white font-bold px-5 sm:px-6 py-3 text-sm sm:text-base tracking-wide transition-all duration-200 group">
                Discuss Your Project
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {relatedServices.length > 0 && (
        <section className="bg-[#F0F4F9] py-16 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-[2px] bg-[#0057B8]" />
                <span className="text-[#0057B8] text-sm font-semibold tracking-widest uppercase">Related Services</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] mb-4">
                Need Help Applying This to a Real Project?
              </h2>
              <p className="text-base text-gray-600 leading-relaxed">
                If this topic matches what your plant, facility, or project team is working on, these are the services most closely related to the article you just read.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {relatedServices.map((service) => (
                <Link
                  key={service.slug}
                  to={`/services/${service.slug}`}
                  className="group bg-white border border-gray-200 hover:border-[#0057B8]/40 hover:shadow-lg transition-all duration-300 p-6 flex flex-col"
                >
                  <h3 className="text-xl font-black text-[#0A0F1E] mb-3 group-hover:text-[#0057B8] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-5 flex-1">
                    {service.description}
                  </p>
                  <div className="inline-flex items-center gap-2 text-[#0057B8] font-semibold text-base group-hover:text-[#003D82] transition-colors">
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between bg-[#0A0F1E] text-white p-8">
              <div>
                <h3 className="text-2xl font-black mb-2">Ready to discuss your automation requirement?</h3>
                <p className="text-base text-gray-300 max-w-2xl">
                  Share your project scope with Hexalogix Automation and we’ll help you choose the right control, monitoring, panel, or commissioning approach.
                </p>
              </div>
              <Link to="/contact" className="w-full sm:w-auto">
                <button className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[#0057B8] hover:bg-[#003D82] text-white font-bold px-5 sm:px-8 py-4 text-sm sm:text-base tracking-wide transition-all duration-200 group">
                  Request a Quote
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#F0F4F9] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-8 h-[2px] bg-[#0057B8]" />
            <span className="text-[#0057B8] text-sm font-semibold tracking-widest uppercase">More Articles</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group block">
                <div className={`h-full bg-gradient-to-br ${p.color} border border-white/5 hover:border-[#0057B8]/50 transition-all duration-300 p-6 flex flex-col relative overflow-hidden`}>
                  <div
                    className="absolute top-0 right-0 w-16 h-16 bg-[#0057B8]/10 group-hover:bg-[#0057B8]/20 transition-colors"
                    style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
                  />
                  <span className="text-[#60A5FA] text-sm font-semibold tracking-wide uppercase mb-3 flex items-center gap-1">
                    <Tag className="w-3 h-3" />{p.category}
                  </span>
                  <h3 className="text-white font-bold text-base leading-snug mb-3 group-hover:text-[#60A5FA] transition-colors flex-1">
                    {p.title}
                  </h3>
                  <div className="flex items-center justify-between border-t border-white/10 pt-3 mt-auto">
                    <span className="text-gray-500 text-sm flex items-center gap-1"><Clock className="w-3 h-3" />{p.readTime}</span>
                    <ArrowRight className="w-4 h-4 text-[#0057B8] group-hover:translate-x-1 group-hover:text-[#60A5FA] transition-all" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
